import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../../store/adminData/adminData";
import { db } from "../../../../../../services/firebaseService/connection";
import {
  doc,
  getDoc,
  getDocs,
  writeBatch,
  arrayUnion,
  collection,
} from "firebase/firestore";
import { toast } from "react-toastify";
import { fetchMembers } from "../../../../../../services/api/members";
import { message, Tag } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import Papa from "papaparse";
import { waitForMilliseconds } from "../../../../../../utils/helper";

const useMemberListUploader = (onCancel) => {
  const MAX_IMPORT_LIMIT = 1000;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const navigate = useNavigate();
  const groupData = useAdminDataStore(selectCurrentGroupData);

  const [showInvalidMembers, setShowInvalidMembers] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [invalidMembers, setInvalidMembers] = useState([]);
  const [validMembers, setValidMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadStats, setUploadStats] = useState({
    total: 0,
    valid: 0,
    invalid: 0,
  });

  const resetState = () => {
    setIsProcessing(false);
    setIsImporting(false);
    setImportProgress(0);
    setInvalidMembers([]);
    setValidMembers([]);
    setAllMembers([]);
    setUploadStats({
      total: 0,
      valid: 0,
      invalid: 0,
    });
  };

  const handleCancel = () => {
    if (isImporting) {
      message.warning("Please wait for the import to complete before closing");
      return;
    }
    onCancel();
    resetState();
  };

  const validateMember = (member, index, validMembersSet) => {
    const errors = [];

    // Name validation
    if (!member.name || typeof member.name !== "string") {
      errors.push("Name is required");
    } else if (member.name.trim().length < 2) {
      errors.push("Name must be at least 2 characters");
    } else if (member.name.trim().length > 70) {
      errors.push("Name longer than 70 characters");
    }

    // Email validation
    if (!member.email || typeof member.email !== "string") {
      errors.push("Email is required");
    } else if (!emailRegex.test(member.email.trim())) {
      errors.push("Invalid email format");
    } else if (member.email.trim().length > 100) {
      errors.push("Email longer than 100 characters");
    }

    if (validMembersSet.has(member.email.trim().toLowerCase())) {
      errors.push("Email already exists in the list");
    } else {
      validMembersSet.add(member.email.trim().toLowerCase());
    }

    return {
      key: index,
      index: index + 1,
      name: member.name?.trim() || "",
      email: member.email?.trim() || "",
      isValid: errors.length === 0,
      errors,
    };
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".csv",
    disabled: isProcessing || isImporting,
    showUploadList: false,
    beforeUpload: async (file) => {
      if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
        message.error("Please upload a CSV file");
        return false;
      }

      setIsProcessing(true);

      await waitForMilliseconds(1000);

      setAllMembers([]);

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        trimHeaders: true,
        transformHeader: (header) => {
          const lower = header.toLowerCase().trim();
          if (lower === "name" || lower === "full name") {
            return "name";
          } else if (lower === "email" || lower === "unique email") {
            return "email";
          } else {
            return `ignored_${header}`;
          }
        },
        complete: (results) => {
          try {
            // Check if required headers exist in the parsed data
            if (results.data.length > 0) {
              const firstRow = results.data[0];
              const hasNameField = Object.prototype.hasOwnProperty.call(
                firstRow,
                "name"
              );
              const hasEmailField = Object.prototype.hasOwnProperty.call(
                firstRow,
                "email"
              );

              if (!hasNameField) {
                message.error(
                  "CSV file must contain a 'Name' or 'Full Name' column"
                );
                setIsProcessing(false);
                return;
              }

              if (!hasEmailField) {
                message.error("CSV file must contain an 'Email' column");
                setIsProcessing(false);
                return;
              }
            }

            if (results.errors.length > 0) {
              console.error("CSV parsing errors:", results.errors);
              message.warning("Some rows had parsing errors");
            }

            // Check if data exceeds maximum import limit
            if (results.data.length > MAX_IMPORT_LIMIT) {
              message.error(
                `File contains ${results.data.length} records. Maximum allowed is ${MAX_IMPORT_LIMIT} records.`
              );
              setInvalidMembers([]);
              setValidMembers([]);
              setAllMembers([]);
              setUploadStats({
                total: 0,
                valid: 0,
                invalid: 0,
              });
              setIsProcessing(false);
              return;
            }

            if (results.data.length === 0) {
              message.error("CSV file is empty or contains no valid data");
              setIsProcessing(false);
              return;
            }

            let validMembersSet = new Set();

            const validatedMembers = results.data.map((member, index) =>
              validateMember(member, index, validMembersSet)
            );

            const invalidMembers = validatedMembers.filter((m) => !m.isValid);
            const validMembers = validatedMembers.filter((m) => m.isValid);

            setInvalidMembers(invalidMembers);
            setValidMembers(validMembers);

            const validCount = validMembers.length;
            const invalidCount = invalidMembers.length;

            setAllMembers(validatedMembers);
            setUploadStats({
              total: validatedMembers.length,
              valid: validCount,
              invalid: invalidCount,
            });
            setIsProcessing(false);

            message.success(`Processed ${validatedMembers.length} records`);
          } catch (error) {
            console.error("Error in complete callback:", error);
            message.error("Error processing CSV data");
            setIsProcessing(false);
          }
        },
        error: (error) => {
          console.error("Error parsing CSV:", error);
          message.error("Error parsing CSV file");
          setIsProcessing(false);
        },
      });
      return false;
    },
  };

  const verifyTwoWayGroupLink = async (
    linkedGroupId,
    currentGroupId,
    isLinkedGroup
  ) => {
    if (isLinkedGroup) {
      const linkedGroup = await getDoc(doc(db, "group", linkedGroupId));
      if (linkedGroup.exists()) {
        const isValid = linkedGroup
          .data()
          ?.linkedGroupId?.includes(currentGroupId);
        return {
          valid: isValid,
          linkedGroupName: linkedGroup.data()?.groupName,
        };
      }
    }
    return { valid: false, linkedGroupName: "" };
  };

  const importMembers = async (
    members,
    groupId,
    isLinkedGroup = false,
    linkedGroupName = ""
  ) => {
    try {
      if (!groupId || !members?.length) {
        return {
          success: false,
          message: "Invalid group ID or no members to import",
          isLinkedGroup,
        };
      }

      setIsImporting(true);
      setImportProgress(10);

      // Step 1: Fetch existing emails from the main invite_emails document
      const inviteEmailsDocRef = doc(db, "invite_emails", groupId);
      const inviteEmailsDoc = await getDoc(inviteEmailsDocRef);

      let existingEmailsArray = [];
      if (inviteEmailsDoc.exists()) {
        existingEmailsArray = inviteEmailsDoc.data().emails || [];
      }

      // Step 2: Fetch all existing user details from userDetails subcollection
      setImportProgress(25);
      const inviteEmailsUserDetails = await getDocs(
        collection(db, "invite_emails", groupId, "userDetails")
      );

      // Create a Set of existing emails from userDetails for O(1) lookup
      const existingUserDetailsEmailsSet = new Set();
      inviteEmailsUserDetails.docs.forEach((doc) => {
        const userData = doc.data();
        if (userData.email) {
          existingUserDetailsEmailsSet.add(userData.email.toLowerCase().trim());
        }
      });

      // Create a Set of existing emails from the main emails array for O(1) lookup
      const existingMainEmailsSet = new Set(
        existingEmailsArray.map((email) => email.toLowerCase().trim())
      );

      // Step 3: Filter out members whose emails already exist in either location
      setImportProgress(40);
      const membersToImport = [];
      const skippedMembers = [];

      for (const member of members) {
        const memberEmail = member.email?.toLowerCase().trim();
        if (memberEmail) {
          // Check if email exists in either the main emails array or userDetails collection
          if (
            existingMainEmailsSet.has(memberEmail) ||
            existingUserDetailsEmailsSet.has(memberEmail)
          ) {
            skippedMembers.push({ ...member, reason: "Email already exists" });
          } else {
            membersToImport.push(member);
          }
        }
      }

      if (membersToImport.length === 0) {
        setIsImporting(false);
        setImportProgress(0);

        // For linked group, we don't need a message (notification handled by primary group)
        const message = !isLinkedGroup
          ? "All emails already exist in the system"
          : "";

        return {
          success: true,
          message,
          imported: 0,
          skipped: skippedMembers.length,
          skippedMembers,
          isLinkedGroup,
        };
      }

      // Step 4: Use Firebase batch to perform atomic updates
      setImportProgress(60);
      const batch = writeBatch(db);

      // Prepare new emails array
      const newEmails = membersToImport.map((member) =>
        member.email.toLowerCase().trim()
      );

      // Update the main invite_emails document with new emails
      if (inviteEmailsDoc.exists()) {
        batch.update(inviteEmailsDocRef, {
          emails: arrayUnion(...newEmails),
        });
      } else {
        batch.set(inviteEmailsDocRef, {
          emails: newEmails,
        });
      }

      // Add each new member to userDetails subcollection
      const userDetailsCollectionRef = collection(
        db,
        "invite_emails",
        groupId,
        "userDetails"
      );

      membersToImport.forEach((member) => {
        const userDetailDocRef = doc(userDetailsCollectionRef);
        const userDetailData = {
          name: member.name.trim(),
          email: member.email.toLowerCase().trim(),
          invitedOn: Date.now(),
        };

        batch.set(userDetailDocRef, userDetailData);
      });

      // Step 5: Commit the batch operation
      setImportProgress(80);
      await batch.commit();

      // Step 6: Get linked group info and import if available
      let linkedGroupInfo = { valid: false, linkedGroupName: "" };
      if (
        !isLinkedGroup &&
        groupData?.linkedGroupId &&
        groupData?.linkedGroupId?.length > 0 &&
        groupId
      ) {
        linkedGroupInfo = await verifyTwoWayGroupLink(
          groupData?.linkedGroupId[0],
          groupId,
          true
        );
        if (linkedGroupInfo.valid) {
          // Recursively import to the linked group
          await importMembers(
            membersToImport,
            groupData?.linkedGroupId[0],
            true,
            linkedGroupInfo.linkedGroupName
          );
        }
      }

      setImportProgress(100);
      setTimeout(() => {
        setIsImporting(false);
        setImportProgress(0);
      }, 500);

      // Generate message with group names (only for primary group)
      let message = "";
      if (!isLinkedGroup) {
        const groupNames = linkedGroupInfo.valid
          ? `${groupData?.groupName} and ${linkedGroupInfo.linkedGroupName}`
          : groupData?.groupName;

        message = `Successfully imported ${membersToImport.length} ${membersToImport.length > 1 ? "members" : "member"} to ${groupNames}`;
      }

      return {
        success: true,
        message,
        imported: membersToImport.length,
        skipped: skippedMembers.length,
        skippedMembers,
        isLinkedGroup, // Return this to handle toast logic
      };
    } catch (error) {
      console.error("Error importing members:", error);
      setIsImporting(false);
      setImportProgress(0);
      return {
        success: false,
        message: "Failed to import members: " + error.message,
        error: error.message,
        isLinkedGroup,
      };
    }
  };

  const handleSaveMembers = async () => {
    if (!groupData?.id) {
      message.error("No group selected");
      return;
    }

    if (!validMembers?.length) {
      message.error("No valid members to import");
      return;
    }

    try {
      const result = await importMembers(validMembers, groupData?.id, false);
      onCancel();

      if (result.success) {
        if (!result.isLinkedGroup) {
          let successMessage = result.message;
          if (result.skipped > 0) {
            successMessage += `\n${result.skipped} ${result.skipped > 1 ? "duplicates" : "duplicate"} skipped`;
          }

          if (result.imported === 0) {
            // Get linked group name if available
            let groupNames = groupData?.groupName;
            if (
              groupData?.linkedGroupId &&
              groupData?.linkedGroupId?.length > 0
            ) {
              const linkedGroupInfo = await verifyTwoWayGroupLink(
                groupData?.linkedGroupId[0],
                groupData?.id,
                true
              );
              if (linkedGroupInfo.valid) {
                groupNames = `${groupData?.groupName} and ${linkedGroupInfo.linkedGroupName}`;
              }
            }
            toast.warning(
              `All uploaded members already exist in ${groupNames}`
            );
          }

          if (result.imported > 0) {
            toast.success(successMessage);
          }
        }

        // Reset the form after successful import
        setValidMembers([]);
        setInvalidMembers([]);
        setAllMembers([]);
        setUploadStats({
          total: 0,
          valid: 0,
          invalid: 0,
        });
        setShowInvalidMembers(false);

        if (result.imported > 0) {
          fetchMembers(groupData?.id);
          navigate(`/admin/members/all`);
        }
      } else {
        toast.error(result.message || "Failed to import members");
      }
    } catch (error) {
      console.error("Error in handleSaveMembers:", error);
      toast.error("An unexpected error occurred while importing members");
    }
  };

  const columns = [
    {
      title: "",
      dataIndex: "index",
      key: "index",
      width: 60,
    },
    {
      title: "Status",
      key: "status",
      width: 80,
      render: (_, record) => {
        return record.isValid ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Valid
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="error">
            Invalid
          </Tag>
        );
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) =>
        name || (
          <span style={{ color: "#ccc", fontStyle: "italic" }}>Empty</span>
        ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) =>
        email || (
          <span style={{ color: "#ccc", fontStyle: "italic" }}>Empty</span>
        ),
    },
    {
      title: "Issues",
      key: "issues",
      width: 100,
      render: (_, record) => {
        return (
          <div>
            {record.errors.map((error, i) => (
              <Tag key={i} color="error" style={{ marginBottom: 4 }}>
                {error}
              </Tag>
            ))}

            {record.isValid && record.errors.length === 0 && <p>-</p>}
          </div>
        );
      },
    },
  ];

  const handleModalCancel = () => {
    if (isImporting) {
      message.warning("Please wait for the import to complete before closing");
      return;
    }
    onCancel();
  };

  return {
    MAX_IMPORT_LIMIT,
    showInvalidMembers,
    isImporting,
    importProgress,
    invalidMembers,
    allMembers,
    uploadStats,
    uploadProps,
    isProcessing,
    columns,
    groupData,
    handleCancel,
    handleSaveMembers,
    handleModalCancel,
    setShowInvalidMembers,
  };
};

export default useMemberListUploader;
